import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
  type UIEvent,
} from "react";
import { flushSync } from "react-dom";

import { useComparator } from "../../comparator.context";
import {
  ComparatorScrollerContext,
  ComparatorScrollerState,
} from "./comparator-scroller.context";
import { useComparatorScrollerConfig } from "./hooks/use-comparator-scroller-config";

type ComparatorScrollerProviderProps = {
  scrollableItemWidth: number;
  maxScrollWidthDisabled?: boolean;
};

type Direction = "prev" | "next";

export function ComparatorScrollerProvider({
  children,
  scrollableItemWidth,
  maxScrollWidthDisabled = false,
}: PropsWithChildren<ComparatorScrollerProviderProps>) {
  const scrollerElementRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<any>();

  const comparator = useComparator();

  // Used to keep track of the current scroll position
  const [scrollLeft, setScrollLeft] = useState<number>(0);
  // Used to prevent multiple scrolls at the same time
  const [locked, setLocked] = useState<boolean>(false);

  const { canScrollPrev, canScrollNext, ...config } =
    useComparatorScrollerConfig({
      maxScrollWidthDisabled,
      scrollerElementRef,
      scrollableItemWidth,
      scrollLeft,
    });

  const getScrollLeftByDirection = useCallback(
    (direction: Direction, value: number): number => {
      if (direction === "prev") {
        return value - scrollableItemWidth - config.scrollerHorizontalPadding;
      }

      return value + scrollableItemWidth + config.scrollerHorizontalPadding;
    },
    [scrollableItemWidth, config.scrollerHorizontalPadding],
  );

  const scrollElementRef = useCallback(
    async (scrollLeft: number) => {
      if (!scrollerElementRef.current) return;

      // Wait for the scroll to finish
      await new Promise((resolve) => setTimeout(resolve, 100));

      scrollerElementRef.current.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    },
    [scrollerElementRef],
  );

  const updateScroll = useCallback(
    (direction: Direction) => {
      if (!scrollerElementRef.current) return;

      let newScrollLeft = scrollerElementRef.current.scrollLeft;

      if (direction === "prev") {
        if (!canScrollPrev) return;

        newScrollLeft = getScrollLeftByDirection("prev", newScrollLeft);
      }

      if (direction === "next") {
        if (!canScrollNext) return;

        newScrollLeft = getScrollLeftByDirection("next", newScrollLeft);
      }

      scrollElementRef(newScrollLeft);
    },
    [
      scrollerElementRef,
      canScrollPrev,
      canScrollNext,
      getScrollLeftByDirection,
      scrollElementRef,
    ],
  );

  const lockScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Set a new timeout to detect the end of scrolling
    scrollTimeoutRef.current = setTimeout(() => {
      setLocked(false);
    }, 100);

    if (locked) return;

    setLocked(true);
  }, [scrollTimeoutRef, locked, setLocked]);

  // Event called while the scroll ref is updated
  const onScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      lockScroll();

      const newScrollLeft = e.currentTarget?.scrollLeft;

      if (
        scrollLeft === newScrollLeft ||
        config.scrollPosition > comparator.selectionCount
      )
        return;

      flushSync(() => setScrollLeft(newScrollLeft));
    },
    [
      scrollLeft,
      config.scrollPosition,
      comparator.selectionCount,
      setScrollLeft,
      lockScroll,
    ],
  );

  const state = useMemo<ComparatorScrollerState>(
    () => ({
      scrollerElementRef,
      scrollLeft,
      scrollPosition: config.scrollPosition,
      locked,
      canScrollPrev,
      canScrollNext,
      maxScrollWidth: config.maxScrollWidth,
      scrollableItemWidth,
    }),
    [
      config,
      scrollerElementRef,
      scrollLeft,
      locked,
      canScrollPrev,
      canScrollNext,
      scrollableItemWidth,
    ],
  );

  return (
    <ComparatorScrollerContext.Provider
      value={{
        state,
        scrollPrev: () => updateScroll("prev"),
        scrollNext: () => updateScroll("next"),
        onScroll,
      }}
    >
      {children}
    </ComparatorScrollerContext.Provider>
  );
}
